import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import type { Observable } from 'rxjs';
import { finalize, map, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import type { EnvironmentFeatures } from '../../environments/environment.types';
import { ALLINDEV_SITE_DATA } from '../config/tenants/allindev/site-data';
import { resolveSiteLogoUrl } from '../config/default-assets';
import type {
  ContactInfoDto,
  ContactSectionDto,
  ImpressumDto,
  FaqSectionDto,
  FeaturesSectionDto,
  IdealStateSectionDto,
  NavItemDto,
  PortfolioSectionDto,
  ProblemSectionDto,
  PricingSectionDto,
  ProcessSectionDto,
  PsrSectionDto,
  QuoteRequestSectionDto,
  ServiceDirectionsSectionDto,
  SiteDataDto,
  SiteProfileDto,
  SiteUiConfig,
  SolutionSectionDto,
  TenantFeaturesDto,
  TenantThemeDto,
  TrustItemDto,
  WebSupervisionSectionDto,
  WhyUsSectionDto,
} from '../models/site-api.model';

@Injectable({ providedIn: 'root' })
export class SiteConfigService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  readonly profile = signal<SiteProfileDto | null>(null);
  readonly contactInfo = signal<ContactInfoDto | null>(null);
  readonly impressum = signal<ImpressumDto | null>(null);
  readonly navigation = signal<NavItemDto[]>([]);
  readonly footerNavigation = signal<NavItemDto[]>([]);
  readonly trustBar = signal<TrustItemDto[]>([]);
  readonly problem = signal<ProblemSectionDto | null>(null);
  readonly idealState = signal<IdealStateSectionDto | null>(null);
  readonly solution = signal<SolutionSectionDto | null>(null);
  readonly features = signal<FeaturesSectionDto | null>(null);
  readonly psr = signal<PsrSectionDto | null>(null);
  readonly portfolio = signal<PortfolioSectionDto | null>(null);
  readonly serviceDirections = signal<ServiceDirectionsSectionDto | null>(null);
  readonly webSupervision = signal<WebSupervisionSectionDto | null>(null);
  readonly pricing = signal<PricingSectionDto | null>(null);
  readonly process = signal<ProcessSectionDto | null>(null);
  readonly quoteRequest = signal<QuoteRequestSectionDto | null>(null);
  readonly whyUs = signal<WhyUsSectionDto | null>(null);
  readonly faq = signal<FaqSectionDto | null>(null);
  readonly contact = signal<ContactSectionDto | null>(null);
  readonly tenantTheme = signal<TenantThemeDto | null>(null);
  readonly tenantFeatures = signal<TenantFeaturesDto | null>(null);
  readonly initialized = signal(false);
  readonly loadFailed = signal(false);

  readonly config = computed<SiteUiConfig>(() => {
    const p = this.profile();
    const tf = this.tenantFeatures();
    const envF: EnvironmentFeatures = environment.features;
    const mergedFeatures: TenantFeaturesDto = {
      trustBar: tf?.trustBar ?? envF.trustBar,
      problem: tf?.problem ?? envF.problem,
      idealState: tf?.idealState ?? envF.idealState,
      solution: tf?.solution ?? envF.solution,
      features: tf?.features ?? envF.features,
      problemSolutionResult: tf?.problemSolutionResult ?? envF.problemSolutionResult,
      portfolio: tf?.portfolio ?? envF.portfolio,
      serviceDirections: tf?.serviceDirections ?? envF.serviceDirections,
      webSupervision: tf?.webSupervision ?? envF.webSupervision,
      pricing: tf?.pricing ?? envF.pricing,
      process: tf?.process ?? envF.process,
      quoteRequest: tf?.quoteRequest ?? envF.quoteRequest,
      whyUs: tf?.whyUs ?? envF.whyUs,
      faq: tf?.faq ?? envF.faq,
      contactForm: tf?.contactForm ?? envF.contactForm,
    };
    return {
      name: p?.name ?? 'ALL-IN-DEV',
      brandName: p?.brandName ?? 'ALL-IN-',
      brandAccent: p?.brandAccent ?? 'DEV',
      logo: resolveSiteLogoUrl(p),
      theme: this.tenantTheme()?.layoutTheme ?? 'classic',
      primaryColor: environment.branding.primaryColor,
      secondaryColor: environment.branding.secondaryColor,
      features: mergedFeatures,
    };
  });

  load(): Observable<void> {
    const data = this.resolveSiteData();
    return of(data).pipe(
      tap(siteData => this.applySiteData(siteData)),
      tap(() => this.applyBranding()),
      finalize(() => this.initialized.set(true)),
      map(() => undefined),
    );
  }

  private resolveSiteData(): SiteDataDto {
    switch (environment.tenantSlug) {
      case 'allindev':
        return ALLINDEV_SITE_DATA;
      default:
        this.loadFailed.set(true);
        return ALLINDEV_SITE_DATA;
    }
  }

  private applySiteData(data: SiteDataDto): void {
    this.profile.set(data.profile);
    this.contactInfo.set(data.contactInfo);
    this.impressum.set(data.impressum);
    this.navigation.set(data.navigation);
    this.footerNavigation.set(data.footerNavigation);
    this.trustBar.set(data.trustBar);
    this.problem.set(data.problem);
    this.idealState.set(data.idealState);
    this.solution.set(data.solution);
    this.features.set(data.features);
    this.psr.set(data.psr);
    this.portfolio.set(data.portfolio);
    this.serviceDirections.set(data.serviceDirections);
    this.webSupervision.set(data.webSupervision);
    this.pricing.set(data.pricing);
    this.process.set(data.process);
    this.quoteRequest.set(data.quoteRequest);
    this.whyUs.set(data.whyUs);
    this.faq.set(data.faq);
    this.contact.set(data.contact);
    this.tenantTheme.set(data.theme);
    if (data.tenantFeatures) {
      this.tenantFeatures.set(data.tenantFeatures);
    }
  }

  private applyBranding(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const root = this.document.documentElement;
    const { primaryColor, secondaryColor } = environment.branding;
    root.style.setProperty('--brand-primary', primaryColor);
    root.style.setProperty('--brand-secondary', secondaryColor);
    root.style.setProperty('--color-accent', primaryColor);
    root.style.setProperty('--color-success', primaryColor);
    const hover = adjustLightness(primaryColor, -8);
    root.style.setProperty('--color-accent-hover', hover);
    root.dataset['layoutTheme'] = this.tenantTheme()?.layoutTheme ?? 'classic';
  }
}

function adjustLightness(hex: string, delta: number): string {
  const m = hex.replace('#', '').trim();
  if (m.length !== 6) {
    return hex;
  }
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  const nr = clamp(r + delta);
  const ng = clamp(g + delta);
  const nb = clamp(b + delta);
  return `#${toHex(nr)}${toHex(ng)}${toHex(nb)}`;
}

function clamp(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function toHex(n: number): string {
  return n.toString(16).padStart(2, '0');
}
